import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft,
  MapPin,
  Brain,
  Flame,
  Compass,
  BookOpen,
  Archive,
  Target,
  Users,
  Check,
  X
} from "lucide-react";

// Feature data for "What's Inside" section
const FEATURES = [
  {
    id: "compass",
    title: "Daily Compass",
    description: "Check-ins that regulate your state. Simple prompts that interrupt unhelpful patterns.",
    icon: Compass
  },
  {
    id: "journal",
    title: "Journal",
    description: "Capture thoughts, decisions, and patterns. Build awareness over time.",
    icon: BookOpen
  },
  {
    id: "reflection-vault",
    title: "Reflection Vault",
    description: "70-page guided journals, reflection prompts, vision board kits, self-awareness tools.",
    icon: Archive
  },
  {
    id: "tools-vault",
    title: "Tools Vault",
    description: "Emotional regulation, cognitive restructuring, anxiety and stress frameworks, identity workbooks.",
    icon: Target
  },
  {
    id: "quests",
    title: "Quests",
    description: "Structured deep-dive pathways when you're ready: Wake Up, Burn the Map, Whole, Purposeful Paths. Not required — there when you need them.",
    icon: Flame
  },
  {
    id: "community",
    title: "Community",
    description: "Shared space for momentum, accountability, and deeper work.",
    icon: Users,
    comingSoon: true
  }
];

// Three columns for "What It Is" section
const WHAT_IT_IS = [
  {
    icon: "📍",
    title: "Anchors your day",
    description: "Daily check-ins and signals that regulate your state"
  },
  {
    icon: "🧠",
    title: "Clears your thinking",
    description: "Space to journal deliberately and build awareness over time"
  },
  {
    icon: "🔥",
    title: "Builds momentum",
    description: "Consistency, streaks, and structured engagement that creates real change"
  }
];

// WHO IT'S FOR statements
const WHO_ITS_FOR = [
  "You feel stuck in patterns you can't break",
  "You know you're capable of more",
  "You need structure, not more information"
];

export default function GrowthStudioPage() {
  const navigate = useNavigate();

  const openWaitlistEmail = () => {
    const subject = encodeURIComponent("Growth Studio Waitlist");
    const body = encodeURIComponent("Hi, I'd like to be added to the Growth Studio waitlist.");
    window.location.href = `mailto:info@truenorth-zone.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 w-10 h-10 rounded-full bg-card/80 backdrop-blur flex items-center justify-center text-foreground hover:bg-card transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* HERO SECTION */}
      <section className="relative px-6 pt-20 pb-16 text-center">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-foreground mb-4 leading-tight">
            Compass doesn't distract you. It realigns you.
          </h1>
          <p className="text-white/70 text-lg sm:text-xl mb-8">
            A daily reset system built to help you break patterns, regain clarity, and move forward with intention.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              className="btn-primary text-lg px-8 py-6"
              onClick={() => navigate("/register")}
              data-testid="start-free-btn"
            >
              Start Free
            </Button>
            <Button 
              variant="outline"
              className="border-foreground/30 text-foreground hover:bg-card text-lg px-8 py-6"
              onClick={openWaitlistEmail}
              data-testid="join-waitlist-btn"
            >
              Join the Waitlist
            </Button>
          </div>
        </div>
      </section>

      {/* WHAT IT IS SECTION */}
      <section className="px-6 py-16 bg-card/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground text-center mb-12">
            Not a content app. Not passive. A structured daily system that guides action.
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {WHAT_IT_IS.map((item) => (
              <div key={item.title} className="text-center">
                <span className="text-4xl mb-4 block">{item.icon}</span>
                <h3 className="text-lg font-heading text-foreground mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE SECTION */}
      <section className="px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground text-center mb-12">
            Everything you need. Nothing you don't.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((feature) => (
              <Card key={feature.id} className="glass-card" data-testid={`feature-${feature.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#a69373]/20 flex items-center justify-center shrink-0">
                      <feature.icon className="w-5 h-5 text-[#a69373]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-foreground">{feature.title}</h3>
                        {feature.comingSoon && (
                          <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/50">Coming Soon</span>
                        )}
                      </div>
                      <p className="text-white/60 text-sm mt-1">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY TEASER SECTION */}
      <section className="px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-heading text-[#a69373] mb-3">
              The Community
            </h2>
            <p className="text-xl text-white/80 mb-4">
              You're not meant to do this alone.
            </p>
            <p className="text-white/60 max-w-2xl mx-auto">
              A shared space for momentum, accountability, and connection. Members support each other, share progress, and go deeper together.
            </p>
            <span className="inline-block mt-4 px-3 py-1 rounded bg-white/10 text-white/50 text-sm">Coming Soon</span>
          </div>
        </div>
      </section>

      {/* WHAT MAKES IT DIFFERENT SECTION */}
      <section className="px-6 py-16 bg-card/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground mb-12">
            Built for people who are done consuming and ready to move.
          </h2>
          <div className="space-y-4 mb-8">
            <p className="text-xl sm:text-2xl text-white/60 flex items-center justify-center gap-3">
              <X className="w-6 h-6 text-red-400" />
              No endless scrolling
            </p>
            <p className="text-xl sm:text-2xl text-white/60 flex items-center justify-center gap-3">
              <X className="w-6 h-6 text-red-400" />
              No passive listening
            </p>
            <p className="text-xl sm:text-2xl text-white/60 flex items-center justify-center gap-3">
              <X className="w-6 h-6 text-red-400" />
              No overwhelm
            </p>
          </div>
          <p className="text-xl sm:text-2xl font-heading text-[#a69373]">
            Just: Daily action. Clear thinking. Real movement.
          </p>
        </div>
      </section>

      {/* WHO IT'S FOR SECTION */}
      <section className="px-6 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground mb-10">
            This was built for you if:
          </h2>
          <div className="space-y-4 mb-8">
            {WHO_ITS_FOR.map((statement) => (
              <p key={statement} className="text-lg sm:text-xl text-white/80 flex items-center justify-center gap-3">
                <Check className="w-5 h-5 text-[#7b9e7b]" />
                {statement}
              </p>
            ))}
          </div>
          <p className="text-sm text-white/40 italic">
            Originally built for high-capacity, polymathic minds. Now built for anyone ready to stop drifting.
          </p>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="px-6 py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-foreground mb-8">
            You don't need more content. You need direction.
          </h2>
          <Button 
            className="btn-primary text-lg px-10 py-6"
            onClick={() => navigate("/register")}
            data-testid="final-cta-btn"
          >
            Start Your Reset
          </Button>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />
    </div>
  );
}
